import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Pane, Text, Button } from 'evergreen-ui';
import { default as Unlayer } from 'react-email-editor';
import { db } from 'src/firebase';
import { getDoc, doc, addDoc, setDoc, collection } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';
import { Loading } from 'src/components';
import AuthContext from 'src/providers/authProvider';
import { getFunctions, httpsCallable } from 'firebase/functions';
import useRedirectIfNotAuthorized from 'src/hooks/useIsAdminWithRedirect';
import '../styles.css';
import SendEmailModal from 'src/components/SendEmailModal';
const functions = getFunctions();
const sendEmail = httpsCallable(functions, 'sendEmail');

const recordEmailSend = async (emailId, email, user) => {
  const docRef = await addDoc(collection(db, 'emailSendRequest'), { emailId, email, userId: user?.uid || 'No user ID found' });
  return docRef.id;
};

const recordEmailSuccess = async (emailId, requestId, email, result, user) => {
  const docRef = await addDoc(collection(db, 'emailSendSuccess'), { requestId: String(requestId), emailId, email, result, userId: user?.uid || 'No user ID found' });
  return docRef.id;
};

const recordEmailFailure = async (emailId, requestId, email, error, user) => {
  const docRef = await addDoc(collection(db, 'emailSendFailure'), { requestId: String(requestId), emailId, email, error, userId: user?.uid || 'No user ID found' });
  return docRef.id;
};

const storage = getStorage();

const getEditorState = (editor) =>
  new Promise((resolve) => {
    if (!editor) {
      return;
    }

    let html = '';
    let txt = '';
    let design = '';
    editor.exportHtml((htmlData) => {
      html = htmlData.html;
      editor.exportPlainText((textData) => {
        txt = textData.plainText;
      });
      editor.saveDesign(async (designData) => {
        design = designData;

        resolve({ html, txt, design });
      });
    });
  });

const EmailEditor = () => {
  const location = useLocation();
  const editorRef = useRef(null);
  const [emailId, setEmailId] = useState(null);
  const [email, setEmail] = useState(null);
  const [emailHtml, setEmailHtml] = useState(null);
  const [emailTxt, setEmailTxt] = useState(null);
  const [emailSendError, setEmailSendError] = useState(null);
  const { isLoading, user } = useContext(AuthContext);
  useRedirectIfNotAuthorized();

  const [isEmailSendInProgress, setIsEmailSendInProgress] = useState(false);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);

  const onSendEmail = useCallback(
    async (request) => {
      setIsEmailSendInProgress(true);
      let requestId;
      try {
        const email = { ...request, html: emailHtml };
        if (emailId !== undefined) {
          email.emailId = emailId;
        }
        if (emailTxt) {
          email.text = emailTxt;
        }
        requestId = await recordEmailSend(emailId, email, user);
        const result = await sendEmail(email);
        await recordEmailSuccess(emailId, requestId, email, result, user);
        setSendEmailModalOpen(false);
      } catch (error) {
        await recordEmailFailure(emailId, requestId, email, JSON.stringify(error), user);
        setEmailSendError(error);
        console.error(error);
      } finally {
        setTimeout(() => setIsEmailSendInProgress(false), 500);
      }
    },
    [email, emailHtml, emailId, emailTxt, user]
  );

  useEffect(() => {
    setEmailId(location.pathname.split('/').pop());
  }, [location.pathname]);

  const editorStateToReactState = useCallback(
    async (saveToDB) => {
      const { txt, html, design } = await getEditorState(editorRef.current?.editor);
      setEmailHtml(html);
      setEmailTxt(txt);
      if (design && saveToDB) {
        const docRef = doc(db, 'emails', emailId);

        const newEmailDoc = { ...email, design: JSON.stringify(design) };

        if (txt) {
          newEmailDoc.text = txt;
        }

        if (html) {
          newEmailDoc.html = html;
        }

        await setDoc(docRef, newEmailDoc);
      }
    },
    [editorRef, email, emailId]
  );

  const onFileUpload = useCallback((drop, done) => {
    const file = drop.attachments[0];
    const storageRef = ref(storage, file.name);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (progress !== 100) {
          done({ progress });
        }
      },
      (error) => {
        alert('Error uploading file: ' + error.message);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        done({ progress: 100, url });
      }
    );
  }, []);

  const getEmailFromDB = useCallback(async () => {
    const docRef = doc(db, 'emails', emailId);
    const docSnap = await getDoc(docRef);
    const newEmail = docSnap.data();
    setEmail(newEmail);
    if (newEmail.design) {
      editorRef.current?.editor?.loadDesign(JSON.parse(newEmail.design));
      editorStateToReactState();
    }
  }, [editorStateToReactState, emailId]);

  useEffect(() => {
    if (emailId) {
      getEmailFromDB();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId]);

  useEffect(() => {
    window.onbeforeunload = function () {
      return 'Your work will be lost.';
    };

    return () => {
      window.onbeforeunload = undefined;
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current?.editor;
    if (editor) {
      editor.addEventListener('design:updated', () => editorStateToReactState());
      editor.registerCallback('image', onFileUpload);
    }
    return () => {
      if (editor) {
        editor.removeEventListener('design:updated', () => editorStateToReactState());
      }
    };
  }, [editorRef, editorStateToReactState, onFileUpload]);

  const onGoBack = useCallback(async () => {
    await editorStateToReactState(true);
    window.history.back();
  }, [editorStateToReactState]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Pane width="100vw" height="100vh" display="flex" justifyContent="center" alignItems="center" flexDirection="column" overflow="hidden">
      {email?.name && (
        <SendEmailModal
          isOpen={sendEmailModalOpen}
          error={emailSendError}
          initialSubject={email.name}
          isEmailSendInProgress={isEmailSendInProgress}
          onClose={() => setSendEmailModalOpen(false)}
          onSend={onSendEmail}
        />
      )}
      <Pane width="100%" backgroundColor="#5C85FF" height="5vh" display="flex" alignItems="center" justifyContent="space-between" padding="20px">
        <Text color="white">Editing Email {email?.name ? `"${email.name}"` : ''}</Text>
        <Pane display="flex" alignItems="center" gap="8px">
          <Button
            appearance="primary"
            onClick={() => {
              setSendEmailModalOpen(true);
            }}
          >
            Send Email
          </Button>
          <Button onClick={onGoBack}>Save & Exit</Button>
        </Pane>
      </Pane>
      <Pane maxWidth="100%" width="100%" overflow="auto">
        <Unlayer minHeight={'95vh'} ref={editorRef} />
      </Pane>
    </Pane>
  );
};

export default EmailEditor;
